import { transparentize, darken } from '@osmosis/shared/styles/colorModifiers';
import React, { forwardRef, ReactNode, JSX} from 'react';
import {
  Button as RebassButton,
  ButtonProps as RebassButtonProps,
  Flex,
  SxStyleProp,
} from 'rebass/styled-components';
import { ColorProps } from '@elsevier-health/osmosis-shared';
import { useColors } from '../../hooks/useColors';
import { LoadingSpinner, LoadingSpinnerProps } from '../LoadingSpinner';
import { Box } from '../Box';

type ButtonType = 'default' | 'outline' | 'transparent';

export type ButtonProps = RebassButtonProps & {
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  children?: ReactNode;
  variant?: ButtonType;
  color?: string;
  bg?: string;
  enabled?: boolean;
  size?: 's' | 'm' | 'l';
  'data-tip'?: boolean;
  'data-for'?: string;
  'data-tooltip-id'?: string;
  'data-testid'?: string;
  disabledSx?: SxStyleProp | undefined;
  isLoading?: boolean;
  loadingSpinnerProps?: LoadingSpinnerProps;
  ariaLabel?: string; // TODO: required ?
  paddingX?: string;
  paddingY?: string;
};

const sizes: {
  [index: string]: { fontSize: number };
} = {
  s: {
    fontSize: 14,
  },
  m: {
    fontSize: 16,
  },
  l: {
    fontSize: 18,
  },
};

export const disabledSx = (bg: string): SxStyleProp | undefined => {
  const styles = { opacity: 0.5, cursor: 'initial', bg };
  return {
    ...styles,
    ':focus,:active,:hover': { ...styles },
  };
};

const getColor = (colors: ColorProps, prop: string) => colors[prop] || prop;

export const getButtonStyles = (
  colors: ColorProps,
  background: string | undefined,
  color: string | undefined,
  isDisabled = false,
  disabledSX: SxStyleProp | undefined,
  type: ButtonType,
): SxStyleProp => {
  const bgProp =
    background || (type === 'default' ? 'primaryText' : 'transparent');
  const colorProp = color || (type === 'default' ? 'textInvert' : 'text');
  const bg = getColor(colors, bgProp);
  const formattedColor = getColor(colors, colorProp);

  switch (type) {
    case 'default':
      return {
        bg,
        color: formattedColor,

        ':hover': {
          bg: darken(0.05, bg),
        },
        ':active': {
          bg: darken(0.1, bg),
        },

        ...(isDisabled ? (disabledSX ? disabledSX : disabledSx(bg)) : {}),
      };
    case 'outline':
      return {
        bg,
        color: formattedColor,
        border: `1px solid ${transparentize(0.75, formattedColor)}`,

        ':hover': {
          bg: formattedColor
            ? transparentize(0.95, formattedColor)
            : transparentize(0.95, '#000'),
        },
        ':active': {
          bg: formattedColor
            ? transparentize(0.92, formattedColor)
            : transparentize(0.92, '#000'),
        },

        ...(isDisabled ? (disabledSX ? disabledSX : disabledSx(bg)) : {}),
      };
    case 'transparent':
      return {
        bg,
        boxShadow: 'none',
        color: formattedColor,

        ':hover': {
          bg: formattedColor
            ? transparentize(0.9, formattedColor)
            : transparentize(0.9, '#000'),
        },
        ':active': {
          bg: formattedColor
            ? transparentize(0.85, formattedColor)
            : transparentize(0.85, '#000'),
        },

        ...(isDisabled ? (disabledSX ? disabledSX : disabledSx(bg)) : {}),
      };
  }
};

type GetButtonStyles = typeof getButtonStyles;

export const variants = {
  outline: getButtonStyles,
  transparent: getButtonStyles,
  default: getButtonStyles,
} as Record<ButtonType, GetButtonStyles>;

export const Button = forwardRef(function Button(
  props: ButtonProps,
  ref,
): JSX.Element {
  const {
    children,
    color: colorProp,
    bg: bgProp,
    sx = {},
    startIcon,
    endIcon,
    variant = 'default',
    size,
    disabled: disabledProp = false,
    enabled = true,
    disabledSx,
    isLoading,
    loadingSpinnerProps,
    ariaLabel,
    paddingX = '1em',
    paddingY = '0.5em',
    ...rest
  } = props;

  const colors = useColors();
  const color = colors[colorProp || ''] || colorProp;
  const bg = colors[bgProp || ''] || bgProp;
  const isDisabled = !enabled || disabledProp || isLoading;
  const formattedVariant = variant as ButtonType;

  return (
    <RebassButton
      disabled={isDisabled}
      variant={formattedVariant}
      ref={ref}
      aria-label={ariaLabel}
      {...rest}
      sx={{
        px: paddingX,
        py: paddingY,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        fontWeight: 'bold',
        ...((size && sizes[size]) || {}),
        ...(variants[formattedVariant]?.(
          colors,
          bg,
          color,
          isDisabled,
          disabledSx,
          formattedVariant,
        ) ?? {}),
        ...sx,
      }}
    >
      {startIcon && !isLoading && (
        <Flex mr={2} alignItems="center">
          {startIcon}
        </Flex>
      )}
      {isLoading ? (
        <Box sx={{ position: 'relative', zIndex: 0 }}>
          <Box opacity={0}>{children}</Box>
          <Box
            sx={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <LoadingSpinner size={16} {...loadingSpinnerProps} />
          </Box>
        </Box>
      ) : (
        children
      )}
      {endIcon && !isLoading && (
        <Flex ml={2} alignItems="center">
          {endIcon}
        </Flex>
      )}
    </RebassButton>
  );
});
